/**
 * useSpaceDesignCollaboration
 *
 * Design-specific collaboration hook for Space Design editor surfaces.
 *
 * Primary responsibilities:
 * - Bootstrap and manage a design collaboration adapter lifecycle
 * - Expose typed awareness and sync helpers to editor surfaces
 *
 * Dependencies:
 * - Supabase browser client
 * - Design collaboration adapter and bootstrap helpers
 */

'use client'

import { useCallback, useEffect, useRef, useState } from 'react'

import { syncBrowserRealtimeAuth } from '@/lib/auth/client-session-recovery'
import {
  createBootstrappingDesignCollaborationState,
  createIdleDesignCollaborationState
} from '@/lib/spaces/design-collaboration/adapter'
import { fetchDesignCollaborationSession } from '@/lib/spaces/design-collaboration/bootstrap'
import { createSupabaseDesignCollaborationAdapter } from '@/lib/spaces/design-collaboration/supabase-adapter'
import type {
  DesignAwarenessState,
  DesignCollaborationAdapter,
  DesignCollaborationState,
  DesignDocumentAdapter,
  DesignOperationEnvelope,
  DesignResyncRequestEnvelope,
  DesignSnapshotEnvelope
} from '@/lib/spaces/design-collaboration/types'
import { createClient } from '@/lib/supabase/client'
import { safeConsole } from '@/lib/utils/safeConsole'

export interface UseSpaceDesignCollaborationOptions {
  designId: string
  documentAdapter?: DesignDocumentAdapter | null
  enabled?: boolean
  initialAwareness?: Partial<DesignAwarenessState>
  onOperation?: (operation: DesignOperationEnvelope) => void
  onResyncRequest?: (request: DesignResyncRequestEnvelope) => void
  onSnapshot?: (snapshot: DesignSnapshotEnvelope) => void
  onStateChange?: (state: DesignCollaborationState) => void
  spaceId: string
}

export interface UseSpaceDesignCollaborationResult {
  awarenessBySession: Record<string, DesignAwarenessState>
  broadcastOperation: DesignCollaborationAdapter['broadcastOperation']
  broadcastSnapshot: DesignCollaborationAdapter['broadcastSnapshot']
  canEdit: boolean
  connectedAt: string | null
  designId: string
  disconnect: () => Promise<void>
  isConnected: boolean
  lastError: string | null
  participants: DesignCollaborationState['participants']
  reconnect: () => Promise<void>
  requestResync: (reason?: string) => Promise<void>
  session: DesignCollaborationState['session']
  setAvailability: DesignCollaborationAdapter['setAvailability']
  status: DesignCollaborationState['status']
  updateAwareness: DesignCollaborationAdapter['updateAwareness']
}

// --------------

const STALE_PENDING_CONNECTION_RECONNECT_MS = 10_000

// --------------

function getErrorMessage(error: unknown) {
  return error instanceof Error
    ? error.message
    : 'Failed to initialize design collaboration'
}

// --------------

function isPersistentDesignCollaborationError(error: unknown) {
  const message = getErrorMessage(error)
  const normalizedMessage = message.toLowerCase()

  return (
    message === 'Unauthorized' ||
    message === 'Forbidden' ||
    message === 'Not Found' ||
    message === 'Invalid design collaboration bootstrap response' ||
    (
      message.startsWith('Failed to bootstrap design collaboration') &&
      /\b(401|403|404)\b/.test(message)
    ) ||
    normalizedMessage.includes('permissions to') ||
    normalizedMessage.includes('permission to') ||
    normalizedMessage.includes('forbidden')
  )
}

// --------------

function isRecoverableDesignCollaborationStatus(
  status: DesignCollaborationState['status'],
  statusStartedAtMs: number
) {
  if (status === 'error' || status === 'closed') {
    return true
  }

  if (status !== 'connecting' && status !== 'bootstrapping') {
    return false
  }

  return Date.now() - statusStartedAtMs >= STALE_PENDING_CONNECTION_RECONNECT_MS
}

// --------------

export function useSpaceDesignCollaboration(
  options: UseSpaceDesignCollaborationOptions
): UseSpaceDesignCollaborationResult {
  const [state, setState] = useState<DesignCollaborationState>(
    createIdleDesignCollaborationState()
  )
  const [connectionAttempt, setConnectionAttempt] = useState(0)
  const isEnabled = options.enabled ?? true

  const adapterRef = useRef<DesignCollaborationAdapter | null>(null)
  const bootstrapEpochRef = useRef(0)
  const documentAdapterRef = useRef<DesignDocumentAdapter | null>(
    options.documentAdapter || null
  )
  const initialAwarenessRef = useRef(options.initialAwareness)
  const operationHandlerRef = useRef(options.onOperation)
  const snapshotHandlerRef = useRef(options.onSnapshot)
  const resyncHandlerRef = useRef(options.onResyncRequest)
  const stateChangeHandlerRef = useRef(options.onStateChange)
  const unsubscribeRef = useRef<(() => void) | null>(null)
  const bootstrapAbortControllerRef = useRef<AbortController | null>(null)
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const reconnectAttemptRef = useRef(0)
  const manualDisconnectRef = useRef(false)
  const suppressAutoReconnectRef = useRef(false)
  const statusRef = useRef<DesignCollaborationState['status']>('idle')
  const statusStartedAtRef = useRef(Date.now())

  const syncStatusRef = (nextStatus: DesignCollaborationState['status']) => {
    if (statusRef.current === nextStatus) {
      return
    }

    statusRef.current = nextStatus
    statusStartedAtRef.current = Date.now()
  }

  useEffect(() => {
    syncStatusRef(state.status)
  }, [state.status])

  useEffect(() => {
    documentAdapterRef.current = options.documentAdapter || null
  }, [options.documentAdapter])

  useEffect(() => {
    initialAwarenessRef.current = options.initialAwareness
  }, [options.initialAwareness])

  useEffect(() => {
    operationHandlerRef.current = options.onOperation
  }, [options.onOperation])

  useEffect(() => {
    snapshotHandlerRef.current = options.onSnapshot
  }, [options.onSnapshot])

  useEffect(() => {
    resyncHandlerRef.current = options.onResyncRequest
  }, [options.onResyncRequest])

  useEffect(() => {
    stateChangeHandlerRef.current = options.onStateChange
  }, [options.onStateChange])

  const clearReconnectTimer = useCallback(() => {
    if (!reconnectTimerRef.current) {
      return
    }

    clearTimeout(reconnectTimerRef.current)
    reconnectTimerRef.current = null
  }, [])

  const cancelBootstrap = useCallback(() => {
    bootstrapEpochRef.current += 1
    bootstrapAbortControllerRef.current?.abort()
    bootstrapAbortControllerRef.current = null
  }, [])

  const disconnectAdapter = useCallback(async (manual: boolean) => {
    manualDisconnectRef.current = manual
    clearReconnectTimer()
    cancelBootstrap()

    const activeAdapter = adapterRef.current
    adapterRef.current = null
    unsubscribeRef.current?.()
    unsubscribeRef.current = null

    if (activeAdapter) {
      await activeAdapter.disconnect()
    }

    syncStatusRef('idle')
    setState(createIdleDesignCollaborationState())
  }, [cancelBootstrap, clearReconnectTimer])

  const disconnect = useCallback(async () => {
    await disconnectAdapter(true)
  }, [disconnectAdapter])

  const reconnect = useCallback(async () => {
    manualDisconnectRef.current = false
    suppressAutoReconnectRef.current = false
    clearReconnectTimer()
    await disconnectAdapter(false)
    setConnectionAttempt(currentAttempt => currentAttempt + 1)
  }, [clearReconnectTimer, disconnectAdapter])

  const updateAwareness = useCallback<
    UseSpaceDesignCollaborationResult['updateAwareness']
  >(async patch => {
    if (!adapterRef.current) {
      return
    }

    await adapterRef.current.updateAwareness(patch)
  }, [])

  const setAvailability = useCallback<
    UseSpaceDesignCollaborationResult['setAvailability']
  >(async availability => {
    if (!adapterRef.current) {
      return
    }

    await adapterRef.current.setAvailability(availability)
  }, [])

  const requestResync = useCallback<
    UseSpaceDesignCollaborationResult['requestResync']
  >(async reason => {
    if (!adapterRef.current) {
      return
    }

    await adapterRef.current.requestResync(reason)
  }, [])

  const broadcastOperation = useCallback<
    UseSpaceDesignCollaborationResult['broadcastOperation']
  >(async operation => {
    if (!adapterRef.current) {
      throw new Error('Design collaboration is not connected')
    }

    await adapterRef.current.broadcastOperation(operation)
  }, [])

  const broadcastSnapshot = useCallback<
    UseSpaceDesignCollaborationResult['broadcastSnapshot']
  >(async (snapshot, snapshotOptions) => {
    if (!adapterRef.current) {
      throw new Error('Design collaboration is not connected')
    }

    await adapterRef.current.broadcastSnapshot(snapshot, snapshotOptions)
  }, [])

  useEffect(() => {
    if (!isEnabled) {
      manualDisconnectRef.current = true
      void disconnectAdapter(true)
      return
    }

    if (!options.spaceId || !options.designId) {
      syncStatusRef('idle')
      setState(createIdleDesignCollaborationState())
      return
    }

    let isCancelled = false
    bootstrapEpochRef.current += 1
    const activeEpoch = bootstrapEpochRef.current
    const abortController = new AbortController()
    bootstrapAbortControllerRef.current = abortController

    manualDisconnectRef.current = false
    suppressAutoReconnectRef.current = false
    syncStatusRef('bootstrapping')
    setState(createBootstrappingDesignCollaborationState())

    const bootstrap = async () => {
      try {
        const session = await fetchDesignCollaborationSession({
          designId: options.designId,
          signal: abortController.signal,
          spaceId: options.spaceId
        })

        if (isCancelled || bootstrapEpochRef.current !== activeEpoch) {
          return
        }

        const nextAdapter = createSupabaseDesignCollaborationAdapter({
          getDocumentAdapter: () => documentAdapterRef.current,
          onOperation: operation => {
            operationHandlerRef.current?.(operation)
          },
          onResyncRequest: request => {
            resyncHandlerRef.current?.(request)
          },
          onSnapshot: snapshot => {
            snapshotHandlerRef.current?.(snapshot)
          },
          session,
          supabase: createClient()
        })

        const unsubscribe = nextAdapter.subscribe(nextState => {
          if (isCancelled || bootstrapEpochRef.current !== activeEpoch) {
            return
          }

          syncStatusRef(nextState.status)
          setState(nextState)
          stateChangeHandlerRef.current?.(nextState)
        })

        unsubscribeRef.current?.()
        unsubscribeRef.current = unsubscribe
        adapterRef.current = nextAdapter
        await nextAdapter.connect()

        if (initialAwarenessRef.current) {
          await nextAdapter.updateAwareness(initialAwarenessRef.current)
        }

        if (isCancelled || bootstrapEpochRef.current !== activeEpoch) {
          unsubscribe()
          if (unsubscribeRef.current === unsubscribe) {
            unsubscribeRef.current = null
          }
          await nextAdapter.disconnect()
          return
        }
      } catch (error) {
        const activeAdapter = adapterRef.current
        adapterRef.current = null
        unsubscribeRef.current?.()
        unsubscribeRef.current = null

        if (activeAdapter) {
          try {
            await activeAdapter.disconnect()
          } catch {}
        }

        if (isCancelled || bootstrapEpochRef.current !== activeEpoch) {
          return
        }

        const message = getErrorMessage(error)
        suppressAutoReconnectRef.current = isPersistentDesignCollaborationError(error)
        syncStatusRef('error')
        setState(currentState => ({
          ...currentState,
          lastError: message,
          status: 'error'
        }))
      } finally {
        if (bootstrapAbortControllerRef.current === abortController) {
          bootstrapAbortControllerRef.current = null
        }
      }
    }

    void bootstrap()

    return () => {
      isCancelled = true
      abortController.abort()
      if (bootstrapAbortControllerRef.current === abortController) {
        bootstrapAbortControllerRef.current = null
      }

      const activeAdapter = adapterRef.current
      adapterRef.current = null
      unsubscribeRef.current?.()
      unsubscribeRef.current = null

      if (activeAdapter) {
        void activeAdapter.disconnect()
      }
    }
  }, [
    connectionAttempt,
    disconnectAdapter,
    isEnabled,
    options.designId,
    options.spaceId
  ])

  useEffect(() => {
    if (!isEnabled) {
      clearReconnectTimer()
      reconnectAttemptRef.current = 0
      return
    }

    if (state.status === 'connected') {
      reconnectAttemptRef.current = 0
      clearReconnectTimer()
      return
    }

    if (manualDisconnectRef.current) {
      clearReconnectTimer()
      return
    }

    if (state.status === 'error' && suppressAutoReconnectRef.current) {
      reconnectAttemptRef.current = 0
      clearReconnectTimer()
      return
    }

    if (state.status !== 'closed' && state.status !== 'error') {
      return
    }

    if (reconnectTimerRef.current) {
      return
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return
    }

    const nextAttempt = reconnectAttemptRef.current + 1
    const reconnectDelayMs = Math.min(1000 * Math.pow(2, nextAttempt - 1), 15000)

    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null
      reconnectAttemptRef.current = nextAttempt
      setConnectionAttempt(currentAttempt => currentAttempt + 1)
    }, reconnectDelayMs)
  }, [clearReconnectTimer, isEnabled, state.status])

  useEffect(() => {
    if (!isEnabled) {
      return
    }

    const supabase = createClient()
    const {
      data: { subscription }
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      const didSyncRealtimeAuth = await syncBrowserRealtimeAuth(session)

      if (!didSyncRealtimeAuth && session?.access_token) {
        safeConsole.warn('Design collaboration realtime auth refresh failed')
      }

      if (
        event !== 'INITIAL_SESSION' &&
        event !== 'SIGNED_IN' &&
        event !== 'TOKEN_REFRESHED'
      ) {
        return
      }

      if (!session?.user || manualDisconnectRef.current) {
        return
      }

      if (
        !isRecoverableDesignCollaborationStatus(
          statusRef.current,
          statusStartedAtRef.current
        )
      ) {
        return
      }

      void reconnect()
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [isEnabled, reconnect])

  useEffect(() => {
    if (!isEnabled) {
      return
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        void setAvailability('hidden')
        return
      }

      void setAvailability('active')
      void requestResync('visibility-change')
    }

    const handleFocus = () => {
      if (
        isRecoverableDesignCollaborationStatus(
          statusRef.current,
          statusStartedAtRef.current
        )
      ) {
        void reconnect()
        return
      }

      if (statusRef.current === 'connected') {
        void setAvailability('active')
        void requestResync('window-focus')
      }
    }

    const handleBlur = () => {
      if (document.visibilityState === 'hidden') {
        return
      }

      void setAvailability('idle')
    }

    const handleOnline = () => {
      if (
        isRecoverableDesignCollaborationStatus(
          statusRef.current,
          statusStartedAtRef.current
        )
      ) {
        void reconnect()
        return
      }

      if (statusRef.current === 'connected') {
        void setAvailability('active')
        void requestResync('browser-online')
      }
    }

    const handleOffline = () => {
      void setAvailability('offline')
    }

    window.addEventListener('focus', handleFocus)
    window.addEventListener('blur', handleBlur)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      window.removeEventListener('focus', handleFocus)
      window.removeEventListener('blur', handleBlur)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
      document.removeEventListener(
        'visibilitychange',
        handleVisibilityChange
      )
    }
  }, [isEnabled, reconnect, requestResync, setAvailability])

  useEffect(() => {
    return () => {
      manualDisconnectRef.current = true
      clearReconnectTimer()
    }
  }, [clearReconnectTimer])

  return {
    awarenessBySession: state.awarenessBySession,
    broadcastOperation,
    broadcastSnapshot,
    canEdit: Boolean(state.session?.permissions.canEdit),
    connectedAt: state.connectedAt,
    designId: options.designId,
    disconnect,
    isConnected: state.status === 'connected',
    lastError: state.lastError,
    participants: state.participants,
    reconnect,
    requestResync,
    session: state.session,
    setAvailability,
    status: state.status,
    updateAwareness
  }
}
